import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { formatDate } from '@angular/common';
import { INVOICE_COMPANY, INVOICE_LOGO } from './invoice.service';

// Generated client-side with pdfmake's browser bundle, same as
// InvoiceService / PurchaseOrderPdfService - see the latter for why this
// isn't generated on the backend instead.
//
// Follows the invoice's layout (same page header, headings, table style),
// but deliberately carries no prices/amounts anywhere - it travels with
// the physical goods, so it only needs enough to pick/pack/check the right
// things: product, SKU, weight, quantity.
export interface DeliverySlipAddress {
  firstName?: string;
  lastName?: string;
  companyName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  postCode?: string;
  country?: string;
  phoneNo?: string;
  email?: string;
}

export interface DeliverySlipPdfInput {
  orderNo: string | number;
  orderDate: string;
  deliveryMethod?: string | null;
  shippingAddress: DeliverySlipAddress | null;
  items: {
    sku: string;
    productName: string;
    quantity: number;
    weight?: number | string | null;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class DeliverySlipPdfService {
  print(order: DeliverySlipPdfInput): void {
    const address = order.shippingAddress;
    const addressLines = address
      ? [
          [address.firstName, address.lastName].filter(Boolean).join(' '),
          address.companyName,
          address.address1,
          address.address2,
          address.city,
          address.country,
          address.postCode,
        ].filter((line) => !!line)
      : [];

    const lineWeight = (item: DeliverySlipPdfInput['items'][number]) =>
      Number(item.weight || 0) * Number(item.quantity || 0);
    const totalWeight = order.items.reduce((sum, item) => sum + lineWeight(item), 0);
    const totalQty = order.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    const dd: any = {
      pageMargins: [40, 90, 40, 40],
      header: [
        {
          margin: [40, 25, 40, 0],
          columns: [
            {
              width: 'auto',
              stack: [
                { image: INVOICE_LOGO, width: 130 },
                {
                  text: `VAT NUMBER : ${INVOICE_COMPANY.vat}`,
                  bold: true,
                  fontSize: 9,
                  margin: [3, 8, 0, 0],
                },
              ],
            },
            {
              width: '*',
              alignment: 'right',
              fontSize: 8,
              margin: [0, 8, 0, 0],
              text: `${INVOICE_COMPANY.name}, ${INVOICE_COMPANY.address}`,
            },
          ],
        },
      ],
      content: [
        { text: `Delivery Slip for order #${order.orderNo}`, style: 'header' },
        {
          margin: [0, 4, 0, 0],
          text: `Order Date: ${order.orderDate ? formatDate(order.orderDate, 'dd-MM-yyyy', 'en-US') : '-'}`,
          style: 'subHeading',
        },
        ...(order.deliveryMethod
          ? [{ margin: [0, 8, 0, 0], text: `Delivery Method: ${order.deliveryMethod}`, style: 'payMethod' }]
          : []),
        { margin: [0, 20, 0, 0], text: 'Shipping Address', style: 'subHeading2' },
        {
          margin: [0, 5, 0, 0],
          text: addressLines.length ? addressLines.join('\n') : 'No shipping address given!',
          style: 'smallLightText',
        },
        {
          margin: [0, 20, 0, 0],
          table: {
            widths: ['22%', '53%', '13%', '12%'],
            headerRows: 1,
            body: [
              [
                { text: 'SKU', style: 'tableHeader', alignment: 'center' },
                { text: 'Product', style: 'tableHeader', alignment: 'center' },
                { text: 'Weight', style: 'tableHeader', alignment: 'center' },
                { text: 'Quantity', style: 'tableHeader', alignment: 'center' },
              ],
              ...order.items.map((item) => [
                { text: item.sku || '-', style: 'tableContent', fontSize: 9 },
                { text: item.productName, style: 'tableContent', fontSize: 9 },
                { text: `${lineWeight(item)} Kg`, style: 'tableContent', alignment: 'center' },
                { text: String(item.quantity), style: 'tableContent', alignment: 'center' },
              ]),
              [
                { colSpan: 3, alignment: 'right', text: 'Total Weight:', style: 'tableRowHeading' },
                {},
                {},
                { alignment: 'center', text: `${totalWeight.toFixed(2)}Kg`, style: 'tableContent' },
              ],
              [
                { colSpan: 3, alignment: 'right', text: 'Total Quantity:', style: 'tableRowHeading' },
                {},
                {},
                { alignment: 'center', text: String(totalQty), style: 'tableContent' },
              ],
            ],
          },
          layout: {
            fillColor: (rowIndex: number) => {
              if (rowIndex % 2 !== 0) return '#f5f5f5';
              if (rowIndex === 0) return '#333333';
              return '';
            },
            paddingLeft: () => 8,
            paddingRight: () => 8,
            paddingTop: () => 3,
            paddingBottom: () => 3,
          },
        },
        { margin: [0, 20, 0, 0], text: 'Customer Details', style: 'subHeading2' },
        { margin: [0, 5, 0, 0], text: `Email: ${address?.email || 'None found'}`, style: 'smallLightText' },
        { margin: [0, 2, 0, 0], text: `Phone: ${address?.phoneNo || 'None found'}`, style: 'smallLightText' },
        {
          margin: [0, 50, 0, 0],
          columns: [
            {
              width: '40%',
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1, lineColor: '#333333' }] },
                { text: 'Packed By', margin: [0, 4, 0, 0], style: 'smallLightText' },
              ],
            },
            { width: '20%', text: '' },
            {
              width: '40%',
              stack: [
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 200, y2: 0, lineWidth: 1, lineColor: '#333333' }] },
                { text: 'Received By (Signature)', margin: [0, 4, 0, 0], style: 'smallLightText' },
              ],
            },
          ],
        },
        {
          margin: [0, 30, 0, 0],
          text: 'If you have any questions regarding this document, please contact us on 01206 700 599 or support@nustone.co.uk.',
          style: 'normalText',
        },
      ],
      styles: {
        header: { fontSize: 16, bold: true },
        subHeading: { fontSize: 14, color: '#9d9d9d' },
        subHeading2: { fontSize: 12, bold: true },
        payMethod: { fontSize: 10, color: '#333333' },
        smallLightText: { fontSize: 10, color: '#51515c' },
        tableHeader: { color: '#f5f5f5', fontSize: 8, bold: true },
        tableRowHeading: { color: '#333333', fontSize: 12, bold: true },
        tableContent: { color: '#333333', fontSize: 11 },
        normalText: { fontSize: 12, color: '#333333' },
      },
    };

    pdfMake
      .createPdf(
        dd,
        {},
        {
          Roboto: {
            normal: 'Roboto-Regular.ttf',
            bold: 'Roboto-Medium.ttf',
            italics: 'Roboto-Italic.ttf',
            bolditalics: 'Roboto-Italic.ttf',
          },
        },
        pdfFonts.pdfMake.vfs,
      )
      .open();
  }
}
